import {
  strategicWinProbability as attackerWinProbability,
  pieceAt,
  threatsAgainst,
  type GameState,
  type Threat,
} from '@archon3d/core';
import type { CellTarget, SessionSnapshot } from '../../game/session';

/**
 * What the board shows about fights that could happen: the odds of the selected piece's attacks
 * and the enemy attacks the AI's own exposure term would fear. Computed from the snapshot here,
 * in the view, so the session stays a plain record of the game.
 */
export interface OddsTarget extends CellTarget {
  /** For an attack: the selected piece's chance of winning the fight on that square. */
  odds?: number;
}

/** The selected piece's targets, each attack carrying its win chance (the AI's probe-state trick). */
export function targetsWithOdds(snapshot: SessionSnapshot): OddsTarget[] {
  const { ctx, state, selectedPieceId, cellTargets } = snapshot;
  const attacker = state.pieces.find((p) => p.id === selectedPieceId);
  if (!attacker) return cellTargets;
  return cellTargets.map((target) => {
    if (target.kind !== 'attack') return target;
    const defender = pieceAt(state, target.x, target.y);
    if (!defender) return target;
    const probe: GameState = {
      ...state,
      phase: 'battle',
      activeFaction: attacker.faction,
      pendingBattle: { attackerId: attacker.id, defenderId: defender.id, x: target.x, y: target.y },
    };
    return { ...target, odds: attackerWinProbability(ctx, probe) };
  });
}

/** States are immutable, so their identity keys the threats the view and the tablets both ask for. */
const threatsByState = new WeakMap<GameState, Threat[]>();

/**
 * Threats to the side to move while a person at this client plays it. The AI needs no warning
 * and a remote player gets their own; between turns (a piece on its way, a fight) nothing shows.
 */
export function threatsToMover(snapshot: SessionSnapshot): Threat[] {
  const { ctx, state, seats } = snapshot;
  if (state.phase !== 'play' || seats[state.activeFaction] !== 'human') return [];
  let threats = threatsByState.get(state);
  if (!threats) {
    threats = threatsAgainst(ctx, state, state.activeFaction);
    threatsByState.set(state, threats);
  }
  return threats;
}

/** The threat to one piece most likely to be carried out: the best odds, then the biggest gain. */
export function worstThreat(
  threats: readonly Threat[],
  pieceId: number | null,
): Threat | undefined {
  let worst: Threat | undefined;
  for (const threat of threats) {
    if (threat.target.id !== pieceId) continue;
    if (
      !worst ||
      threat.probability > worst.probability ||
      (threat.probability === worst.probability && threat.gain > worst.gain)
    )
      worst = threat;
  }
  return worst;
}
